/* eslint-disable import/no-cycle */
import { isAppliedCodeValid } from '../common/cart';
import { mutexManager } from '../common/mutexmanager';
import {
  getFGDiscountCodeChecked,
  getIsShopifyFunctionsEnabled,
  getMonkAppliedCodes,
  getMrDiscountCode,
  getMrFreegiftDiscountCode,
  getShopifyMonkCodesMapping,
  isFGRuleCodeAlreadyApplied,
  resetAutoAddedVariantsDataWithRepitionCount,
  setFGDiscountCodeChecked,
} from '../common/settings';
import { executeFreegiftConditions } from '../freegift';
import {
  CartPageDiscountConfigType,
  OfferResponseType,
  OfferType,
  WidgetType,
} from './commonTypes';

export function filterWidgets({
  offerTypes,
  displayType,
  widgets,
  availingType,
  triggerTypes,
}: {
  offerTypes: string[];
  displayType: string;
  widgets: WidgetType[];
  availingType: 'manual_section' | 'auto_add';
  triggerTypes?: string[];
}): WidgetType[] {
  if (!triggerTypes) {
    return widgets.filter(
      widget =>
        offerTypes.includes(widget.offerType) &&
        widget.displayType === displayType &&
        widget.availingType === availingType,
    );
  }
  return widgets.filter(
    widget =>
      offerTypes.includes(widget.offerType) &&
      widget.displayType === displayType &&
      widget.availingType === availingType &&
      triggerTypes.includes(widget.triggerType),
  );
}

// Checks if the offer is valid for the current page based on the widget's triggerPage URLs.
export function isOfferValidForCurrentPage({
  widget,
}: {
  widget: WidgetType;
}): boolean {
  const { triggerPage } = widget;
  return triggerPage
    .split(',')
    .some(item => item && window.location.href.includes(item.trim()));
}

export const checkCodeIsValid: (
  offerCodes: string[],
) => boolean = offerCodes => {
  const mrCodes = getMonkAppliedCodes();
  // add code from url to mrCodes if freegift code is not applied
  const preAppliedCodeThroughUrl = getMrFreegiftDiscountCode();
  if (preAppliedCodeThroughUrl && !isFGRuleCodeAlreadyApplied()) {
    mrCodes.push(preAppliedCodeThroughUrl.toUpperCase());
  }
  if (mrCodes) {
    // mrCodes = JSON.parse(mrCodes);
    if (Array.isArray(mrCodes)) {
      for (let i = 0; i < offerCodes.length; i += 1) {
        if (mrCodes.includes(offerCodes[i].toString().toUpperCase())) {
          return true;
        }
      }
    }
    return false;
  }
  return false;
};

export const isFreegiftCodeValid: (
  appliedCode: string,
  freegiftDiscountCodes: string[],
) => boolean = (appliedCode, freegiftDiscountCodes) => {
  if (getIsShopifyFunctionsEnabled()) {
    return checkCodeIsValid(freegiftDiscountCodes);
  }
  return isAppliedCodeValid({
    appliedCode,
    freegiftDiscountCodes,
  });
};

// as FG offer eval and cart discount are checked in parallel
// offer might not be created so the cart discount will not display
// discount code, this function is used to ensure discount code display is checked
// once for every offer after offer creation
export const checkAndRunCartDiscountForDiscountCodeOffer = async ({
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  offerID,
}: {
  cartPageDiscountConfig: CartPageDiscountConfigType;
  isDiscountCodeEnabled: boolean;
  offerID: string;
}): Promise<void> => {
  // flag is set so don't check
  if (getFGDiscountCodeChecked({ offerID })) {
    return;
  }

  // set the flag so it is not checked again
  setFGDiscountCodeChecked({ offerID });
  const cartDiscountModule = await import('../cartDiscount');
  cartDiscountModule.checkAndUpdateCartDiscount({
    cartPageDiscountConfig,
    isDiscountCodeEnabled,
    apiCart: undefined,
    discountCode: undefined,
  });
};

export const isFreegiftWithCodeEligible = ({
  offerType,
  is_eligible,
  offer,
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
}: {
  offerType: string;
  is_eligible: boolean;
  offer: OfferType;
  cartPageDiscountConfig: CartPageDiscountConfigType;
  isDiscountCodeEnabled: boolean;
}) => {
  let isFreegift = false;
  let isEligible = is_eligible;
  // updating eligibility status if freegiftDiscountCode is applied
  if (
    offerType === 'freegift' &&
    is_eligible &&
    offer.isFreegiftDiscountCodeEnabled
  ) {
    const appliedCode = getMrDiscountCode();

    if (!isFreegiftCodeValid(appliedCode, offer.freegiftDiscountCodes)) {
      isFreegift = true;
      isEligible = false;
      // check discount code display once for each fg offer created
      checkAndRunCartDiscountForDiscountCodeOffer({
        cartPageDiscountConfig,
        isDiscountCodeEnabled,
        offerID: offer.mr_offer_id,
      });
    }
  }
  return { is_eligible: isEligible, isFreegift };
};

export const addCodeToMapping = (
  response: OfferResponseType,
  widget: WidgetType,
  mrDiscountCodesMap: any,
) => {
  // response.offer.freegiftDiscountCodes has atleast one code, then only store mapping in sessionStorage.
  if (response.offer.freegiftDiscountCodes.length) {
    const funnelNames = Object.keys(mrDiscountCodesMap);
    // if mapping for perticular offer already present then delete it first
    // add new mapping for perticular offer after deleting previous mapping data.
    funnelNames.forEach(code => {
      if (
        mrDiscountCodesMap[code] &&
        mrDiscountCodesMap[code].widgetID === widget.id
      ) {
        delete mrDiscountCodesMap[code];
      }
    });
    mrDiscountCodesMap[
      response.offer.shopifyFunctionsDiscountTitle.toUpperCase()
    ] = {
      codes: response.offer.freegiftDiscountCodes.map(code =>
        code.toUpperCase(),
      ),
      productIds: [],
      widgetID: widget.id,
    };
    const productIds = [];
    response.offer.offered.forEach(({ variants }) => {
      variants.forEach(variant => {
        productIds.push(variant.id);
      });
    });
    mrDiscountCodesMap[
      response.offer.shopifyFunctionsDiscountTitle.toUpperCase()
    ].productIds = productIds;
    sessionStorage.setItem(
      'mrDiscountCodesMap',
      JSON.stringify(mrDiscountCodesMap),
    );
  }
};

export const removeCodeFromMapping = (
  widget: WidgetType,
  mrDiscountCodesMap: any,
) => {
  const funnelNames = Object.keys(mrDiscountCodesMap);
  let inEligibleFunnelTitle;
  for (let i = 0; i < funnelNames.length; i += 1) {
    if (mrDiscountCodesMap[funnelNames[i]].widgetID === widget.id) {
      inEligibleFunnelTitle = funnelNames[i];
      break;
    }
  }
  if (inEligibleFunnelTitle) {
    delete mrDiscountCodesMap[inEligibleFunnelTitle];
  }
  sessionStorage.setItem(
    'mrDiscountCodesMap',
    JSON.stringify(mrDiscountCodesMap),
  );
};

export const setFreeGiftDiscountCodesMapping = async (
  response: OfferResponseType,
  widget: WidgetType,
) => {
  const fgMutex = mutexManager.getMutex('setFreeGiftDiscountCodesMapping');

  try {
    await fgMutex.acquire();
    // setting FG funnel title with funnel codes mapping in session storage for future use
    const mrDiscountCodesMapData = getShopifyMonkCodesMapping();
    if (response.is_eligible) {
      addCodeToMapping(response, widget, mrDiscountCodesMapData);
      return;
    }
    removeCodeFromMapping(widget, mrDiscountCodesMapData);
  } finally {
    fgMutex.release();
  }
};

// function to be called whenever an emebeded offer is eligible
export async function onModuleOfferEligible({ offer }: { offer: OfferType }) {
  // if functions is enabled then store data
  if (getIsShopifyFunctionsEnabled()) {
    const sfModule = await import('./shopifyFunctionsDiscount');
    sfModule.storeShopifyFunctionsOfferData({ offer });
  }
}

export const onFreegiftNotEligibleResponse = async ({
  offerType,
  availingType,
  mrCart,
  widget,
  reservedItemQuantity,
  useExistingConfig = false,
  existingConfig,
}: {
  offerType: string;
  availingType: string;
  mrCart: any;
  widget: WidgetType;
  reservedItemQuantity?: {
    [key: number]: number;
  };
  useExistingConfig?: boolean;
  existingConfig?: any;
}) => {
  if (offerType !== 'freegift') {
    return;
  }

  await executeFreegiftConditions({
    mrCart,
    availingType,
    widget,
    isEligible: false,
    reservedItemQuantity,
    useExistingConfig,
    existingConfig,
    canBeAutoRemoved: undefined,
  });

  resetAutoAddedVariantsDataWithRepitionCount({ widgetID: widget.id });
};
